const YOUTUBE_HOSTS = new Set(['youtube.com', 'www.youtube.com', 'm.youtube.com', 'youtu.be', 'youtube-nocookie.com', 'www.youtube-nocookie.com'])
const VIMEO_HOSTS = new Set(['vimeo.com', 'www.vimeo.com', 'player.vimeo.com'])

export function getVideoUrlValue(video) {
  if (!video) return ''
  if (typeof video === 'string') return video.trim()
  return (video.url || video.videoUrl || '').trim()
}

export function isExternalVideoUrl(video) {
  const value = getVideoUrlValue(video)
  if (!value) return false

  const lower = value.toLowerCase()
  if (lower.startsWith('blob:')) return false
  if (typeof video === 'string') return /^https?:\/\//i.test(value)
  if (video?.source === 'url') return /^https?:\/\//i.test(value)
  if (video?.source === 'upload' || video?.provider === 'upload' || video?.publicId) return false
  if (lower.includes('/uploads/') || lower.includes('res.cloudinary.com') || lower.includes('cloudinary.com')) return false
  return /^https?:\/\//i.test(value)
}

export function isUploadedVideo(video) {
  if (!video) return false
  const value = getVideoUrlValue(video)
  if (!value) return false
  return !isExternalVideoUrl(video)
}

function getSafeUrl(value) {
  if (typeof value !== 'string' || !value.trim()) return null

  try {
    const url = new URL(value.trim())
    if (!['http:', 'https:'].includes(url.protocol)) return null
    return url
  } catch {
    return null
  }
}

function parseVideoUrl(value) {
  const originalValue = typeof value === 'string' ? value.trim() : ''
  const url = getSafeUrl(originalValue)
  if (!url) return { valid: false, message: 'Enter a valid http(s) video URL.' }

  const hostname = url.hostname.toLowerCase()
  let provider = 'direct'
  let videoId = ''

  if (YOUTUBE_HOSTS.has(hostname)) {
    provider = 'youtube'
    if (hostname === 'youtu.be') videoId = url.pathname.slice(1).split('/')[0]
    else if (url.pathname === '/watch') videoId = url.searchParams.get('v') || ''
    else if (url.pathname.startsWith('/shorts/')) videoId = url.pathname.split('/')[2] || ''
    else if (url.pathname.startsWith('/embed/')) videoId = url.pathname.split('/')[2] || ''

    if (!videoId || !/^[A-Za-z0-9_-]{6,}$/.test(videoId)) {
      return { valid: false, message: 'Enter a valid YouTube or YouTube Shorts URL.' }
    }

    return {
      valid: true,
      url: originalValue,
      provider,
      videoId,
      embedUrl: `https://www.youtube-nocookie.com/embed/${videoId}?rel=0&playsinline=1`,
      thumbnail: `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`,
      source: 'url'
    }
  }

  if (VIMEO_HOSTS.has(hostname)) {
    provider = 'vimeo'
    const match = url.pathname.match(/\/(?:video\/)?(\d+)/)
    videoId = match?.[1] || ''
    if (!videoId) return { valid: false, message: 'Enter a valid Vimeo URL.' }

    return {
      valid: true,
      url: originalValue,
      provider,
      videoId,
      embedUrl: `https://player.vimeo.com/video/${videoId}`,
      thumbnail: '',
      source: 'url'
    }
  }

  return { valid: true, url: originalValue, provider: 'external', embedUrl: '', thumbnail: '', source: 'url' }
}

export function resolveExternalVideo(video) {
  const originalUrl = getVideoUrlValue(video)
  if (!originalUrl) {
    return { valid: false, supported: false, provider: 'invalid', mediaType: 'video', playbackType: 'none', playbackUrl: '', originalUrl: '' }
  }

  const parsed = parseVideoUrl(originalUrl)
  if (!parsed.valid) {
    return { ...parsed, supported: false, provider: 'invalid', mediaType: 'video', playbackType: 'none', playbackUrl: '', originalUrl }
  }

  if (parsed.provider === 'youtube' || parsed.provider === 'vimeo') {
    return { ...parsed, supported: true, mediaType: 'video', playbackType: 'embed', playbackUrl: parsed.embedUrl, originalUrl }
  }

  const path = new URL(originalUrl).pathname.toLowerCase()
  const directVideoExtension = /\.(mp4|webm|ogg|ogv|mov|m4v|mpeg|mpg|3gp|3g2|avi|mkv|flv|wmv)$/.test(path)
  if (directVideoExtension) {
    return { ...parsed, provider: 'direct', supported: true, mediaType: 'video', playbackType: 'html5', playbackUrl: originalUrl, originalUrl }
  }

  return { ...parsed, supported: false, mediaType: 'video', playbackType: 'none', playbackUrl: '', originalUrl }
}

export { parseVideoUrl }
